import {
  ALTERNATE_PAYMENT_FLOW_OPTIONS,
  DonateModalAction,
} from "@components/donate/DonateV3/types";
import { useGateValue } from "@statsig/react-bindings";
import { Big } from "big.js";
import { useMemo } from "react";

import { GivingCreditResponse } from "@every.org/common/src/codecs/entities";
import {
  GIFT_CARDS_NONPROFIT_ID,
  QCD_IRA_CHECK_GATE,
} from "@every.org/common/src/entity/constants";
import { DonationFlowPaymentOption } from "@every.org/common/src/entity/types";
import { disablePaymentMethodsSpec } from "@every.org/common/src/entity/types/configVariable";

import {
  useConfigVariable,
  UseConfigVariableStatus,
} from "src/context/ConfigVariableContext";
import { ContextNonprofit } from "src/context/NonprofitsContext/types";
import { paymentFlowOptionsFromString } from "src/utility/helpers";
import { isWebview } from "src/utility/isWebview";
import { logger } from "src/utility/logger";

const DISABLED_OPTIONS_FOR_UPDATE_DONATION = [
  ...ALTERNATE_PAYMENT_FLOW_OPTIONS,
  DonationFlowPaymentOption.PAYMENT_REQUEST,
  DonationFlowPaymentOption.PAYPAL,
  DonationFlowPaymentOption.VENMO,
];

const DISABLED_OPTIONS_FOR_GIFT_CARDS = [
  ...ALTERNATE_PAYMENT_FLOW_OPTIONS,
  DonationFlowPaymentOption.PAYPAL,
  DonationFlowPaymentOption.VENMO,
  DonationFlowPaymentOption.GIFT_CARD,
];

const ONE_TIME_PAYMENT_FLOW_OPTIONS = [
  DonationFlowPaymentOption.CRYPTO,
  DonationFlowPaymentOption.DAF,
  DonationFlowPaymentOption.IRA,
  DonationFlowPaymentOption.STOCKS,
  DonationFlowPaymentOption.VENMO,
  DonationFlowPaymentOption.GIFT_CARD,
];

const GIVING_CREDIT_PAYMENT_FLOW_OPTIONS = [
  DonationFlowPaymentOption.CREDIT_CARD,
  DonationFlowPaymentOption.BANK,
];

function addToSet<T>(set: Set<T>, values: Array<T> | Set<T>) {
  values.forEach((elem) => set.add(elem));
}

function deleteFromSet<T>(set: Set<T>, values: Array<T> | Set<T>) {
  values.forEach((elem) => set.delete(elem));
}

export const usePaymentFlowOptions = ({
  nonprofit,
  paymentMethodsFromUrl,
  successUrl,
  donateAction,
  lockRecurringFrequency,
  availableGivingCredit,
  inaccessibleGivingCredit,
  canUseCredits,
  givingCreditToRedeem,
}: {
  nonprofit: ContextNonprofit;
  paymentMethodsFromUrl?: string;
  successUrl?: string;
  donateAction: DonateModalAction;
  lockRecurringFrequency: boolean;
  availableGivingCredit?: Big;
  inaccessibleGivingCredit?: Big;
  canUseCredits?: boolean;
  givingCreditToRedeem?: GivingCreditResponse;
}) => {
  const disabledPaymentMethods = useConfigVariable(disablePaymentMethodsSpec);

  const isIraCheckEnabled = useGateValue(QCD_IRA_CHECK_GATE);

  const paymentFlowOptions = useMemo(() => {
    // IRA is gated: it is only a selectable option when the gate is on.
    const availableDonationFlowPaymentOptions = Object.values(
      DonationFlowPaymentOption
    ).filter(
      (option) => isIraCheckEnabled || option !== DonationFlowPaymentOption.IRA
    );

    const paymentOptions = new Set<DonationFlowPaymentOption>([]);
    const disabledPaymentFlowOptions =
      disabledPaymentMethods.status === UseConfigVariableStatus.SUCCESS
        ? paymentFlowOptionsFromString(disabledPaymentMethods.value)
        : [];

    if (paymentMethodsFromUrl) {
      addToSet(
        paymentOptions,
        paymentFlowOptionsFromString(paymentMethodsFromUrl).filter((option) =>
          availableDonationFlowPaymentOptions.includes(option)
        )
      );
    } else {
      addToSet(paymentOptions, availableDonationFlowPaymentOptions);
    }

    if (lockRecurringFrequency) {
      deleteFromSet(paymentOptions, ONE_TIME_PAYMENT_FLOW_OPTIONS);
    }

    deleteFromSet(paymentOptions, disabledPaymentFlowOptions);

    if (donateAction === DonateModalAction.UPDATE) {
      deleteFromSet(paymentOptions, DISABLED_OPTIONS_FOR_UPDATE_DONATION);
    }

    if (nonprofit.id === GIFT_CARDS_NONPROFIT_ID) {
      deleteFromSet(paymentOptions, DISABLED_OPTIONS_FOR_GIFT_CARDS);
    }

    if (isWebview()) {
      deleteFromSet(paymentOptions, [DonationFlowPaymentOption.VENMO]);
    }

    if (
      (availableGivingCredit?.gt(0) || inaccessibleGivingCredit?.gt(0)) &&
      !givingCreditToRedeem
    ) {
      const hasOnlyGiftCardOption =
        paymentOptions.size === 1 &&
        paymentOptions.has(DonationFlowPaymentOption.GIFT_CARD);

      deleteFromSet(paymentOptions, [DonationFlowPaymentOption.GIFT_CARD]);

      if (hasOnlyGiftCardOption) {
        addToSet(
          paymentOptions,
          GIVING_CREDIT_PAYMENT_FLOW_OPTIONS.filter(
            (option) => !disabledPaymentFlowOptions.includes(option)
          )
        );
      }
    }

    if (paymentOptions.size < 1) {
      logger.warn({
        message: "Donation flow has no valid payment options.",
        data: { paymentOptions, href: location.href },
      });
    }

    return paymentOptions;
  }, [
    paymentMethodsFromUrl,
    disabledPaymentMethods,
    donateAction,
    nonprofit.id,
    lockRecurringFrequency,
    availableGivingCredit,
    inaccessibleGivingCredit,
    givingCreditToRedeem,
    isIraCheckEnabled,
  ]);

  return paymentFlowOptions;
};
